/**
 * Durchgehender Buchungsbalken über dem Tagesraster (Board „Vertrieb“): Farbe und Name der
 * Quelle, Pfeile, wo der Aufenthalt über das Fenster hinausgeht, und die geplante Abreise.
 * Überschneiden sich zwei Aufenthalte, liegen sie in zwei schmaleren Bahnen.
 *
 * Same-Day-Wechsel nutzt die Gegen-Diagonal-Geometrie des Belegungskalenders
 * (`computePillLayout`). Das Lesemodell trägt bewusst keine Gast- oder Buchungsidentität: der
 * Balken informiert, er verlinkt nicht.
 */
import { CalendarClock, ChevronLeft, ChevronRight } from 'lucide-react';
import { computePillLayout } from '@/features/calendar/pillLayout';
import { cn } from '@/shared/lib/cn';
import { dayAndMonth } from './calendarFormat';
import type { BookingBar as BookingBarModel, BookingSource } from './calendarModel';

const SOURCE_STYLE: Record<BookingSource, { label: string; short: string; className: string }> = {
  BookingCom: { label: 'Booking.com', short: 'Booking.com', className: 'bg-navy text-white' },
  Airbnb: { label: 'Airbnb', short: 'Airbnb', className: 'bg-sealwax text-white' },
  Manual: { label: 'Manuelle Buchung', short: 'Manuell', className: 'bg-amber text-ink' },
  Other: { label: 'Andere Buchung', short: 'Andere', className: 'bg-ink-soft text-white' },
};

export function BookingBar({
  bar,
  gridStart,
  twoLanes = false,
}: {
  bar: BookingBarModel;
  gridStart: number;
  /** Die Zeile hat überlappende Aufenthalte: jeder Balken in seiner Bahn, schmaler. */
  twoLanes?: boolean;
}) {
  const style = SOURCE_STYLE[bar.source];
  const layout = computePillLayout(bar, gridStart);
  const period = `${dayAndMonth(bar.start)} – ${dayAndMonth(bar.endExclusive)}`;
  const planned = bar.earlyDeparturePlan?.plannedDepartureDate;
  const accessible = `${style.label}, ${period}${planned ? `, geplante Abreise ${dayAndMonth(planned)}` : ''}`;
  const squared = bar.hasTransitionIn || bar.hasTransitionOut;
  return (
    // contain:inline-size — im min-w-max-Raster verbreiterte der Text sonst jede Tagesspalte.
    <div
      className={cn(
        'pointer-events-none relative z-10 flex h-full px-0.5 [contain:inline-size]',
        twoLanes ? (bar.lane ? 'items-end pb-2' : 'items-start pt-2') : 'items-center',
      )}
      style={{ gridColumn: layout.gridColumn, gridRow: 1 }}
    >
      <div
        className={cn(
          'flex w-full min-w-0 items-center gap-1.5 px-2.5 text-[11px] font-bold',
          twoLanes ? 'h-[22px]' : 'h-7',
          style.className,
          !squared && !bar.clippedStart && 'rounded-l-lg',
          !squared && !bar.clippedEnd && 'rounded-r-lg',
        )}
        style={layout.clipPath ? { clipPath: layout.clipPath } : undefined}
        title={accessible}
        aria-label={accessible}
      >
        <span
          className="flex min-w-0 items-center gap-1.5"
          style={{ paddingLeft: layout.contentPadStart, paddingRight: layout.contentPadEnd }}
        >
          {bar.clippedStart && <ChevronLeft size={12} aria-hidden className="shrink-0" />}
          <span className="min-w-0 truncate">{style.short}</span>
          {planned && (
            <span className="inline-flex shrink-0 items-center gap-1">
              <CalendarClock size={12} aria-hidden /> Plan {dayAndMonth(planned)}
            </span>
          )}
          {bar.clippedEnd && <ChevronRight size={12} aria-hidden className="shrink-0" />}
        </span>
      </div>
    </div>
  );
}

export function bookingSourceLabel(source: BookingSource): string {
  return SOURCE_STYLE[source].label;
}
